import { Tabs, Text, Title } from '@mantine/core';
import { CrudSection } from '../components/CrudSection';
import { EntityLabel } from '../components/EntityField';
import { useLocationId } from '../hooks/useSeedLocation';
import { useTabInUrl } from '../hooks/useTabInUrl';
import { enumOptions } from '../lib/enums';
import { formatDate, formatMoney, formatRateBp } from '../lib/format';
import { OrdersArriving } from './channels/OrdersArriving';
import { OwedByPlatform } from './channels/OwedByPlatform';
import {
  expectedNetMinor,
  heldBackMinor,
  SettlementOutcome,
  SettlementPaymentButton,
} from './channels/SettlementPayment';
import { LinesFromOrders } from './channels/UnmatchedLines';

const RECEIVABLES_PATH = '/admin/v1/channels/receivables';

export function ChannelsPage() {
  const locationId = useLocationId();
  // In the address, so "match this" can send somebody to the matching tab and a link can point at
  // the money rather than at whichever tab happens to be first.
  const [tab, setTab] = useTabInUrl(
    ['channels', 'arriving', 'receivables', 'settlements', 'mappings'] as const,
    'channels',
  );
  return (
    <>
      <Title order={3} mb="md">
        Delivery platforms
      </Title>
      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="channels">Where sales come from</Tabs.Tab>
          {/* Second, not last. Once a platform is sending its own orders this is the tab that gets
              opened daily, and the three after it are all monthly work against a statement. */}
          <Tabs.Tab value="arriving">Orders arriving</Tabs.Tab>
          <Tabs.Tab value="receivables">Owed to you</Tabs.Tab>
          <Tabs.Tab value="settlements">Payouts</Tabs.Tab>
          <Tabs.Tab value="mappings">Menu matching</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="channels">
          <CrudSection
            title="Where sales come from"
            entityNoun="sales channel"
            description="Your own bar, plus any delivery platform you sell through. A platform keeps the money until it pays you, so those sales count as money owed rather than cash in the drawer."
            listPath="/admin/v1/channels/channels"
            updatePathTemplate="/admin/v1/channels/channels/{id}"
            defaults={{ locationId }}
            rowLabel={(r) => String(r.name ?? 'this channel')}
            emptyMessage="Nothing set up yet. Add your bar, and each delivery platform you sell through."
            columns={[
              { accessor: 'name', title: 'Name' },
              { accessor: 'type', title: 'Kind', enumGroup: 'channelKind' },
              { accessor: 'settlementMode', title: 'When you get paid', enumGroup: 'settlementMode' },
              {
                accessor: 'commissionBp',
                title: 'Their cut',
                // Your own bar takes no commission from itself. "0%" there reads as a rate somebody
                // negotiated rather than a question that does not apply.
                render: (r) =>
                  r.type === 'deliveryPlatform' ? (
                    formatRateBp(r.commissionBp as number)
                  ) : (
                    <Text c="dimmed">—</Text>
                  ),
              },
              {
                // Which prices a platform charges from is the setting with money in it — delivery
                // menus are usually priced up to cover the commission — and it was the one thing
                // you could set here and then never see again without reopening every channel.
                accessor: 'priceListId',
                title: 'Prices used',
                render: (r) =>
                  r.priceListId ? (
                    <EntityLabel entity="priceList" id={r.priceListId} />
                  ) : (
                    <Text size="sm" c="dimmed">
                      Your normal menu
                    </Text>
                  ),
              },
            ]}
            fields={[
              {
                name: 'name',
                label: 'Name',
                type: 'text',
                required: true,
                description: 'What you call it — the name staff will see on the register',
              },
              {
                name: 'type',
                label: 'Kind',
                type: 'select',
                options: enumOptions('channelKind'),
              },
              {
                name: 'settlementMode',
                label: 'When you get paid',
                type: 'select',
                options: enumOptions('settlementMode'),
              },
              {
                name: 'commissionBp',
                label: 'Their cut',
                type: 'number',
                unit: 'percent',
                description: 'How much of each order the platform keeps',
              },
              {
                name: 'priceListId',
                label: 'Prices to use',
                type: 'entity',
                resource: 'priceList',
                nullable: true,
                description:
                  'Charge platform orders from their own price list — often higher, to cover the commission. Leave empty to use your normal menu prices.',
              },
              {
                name: 'honoredPriceListIds',
                label: 'Specials that still apply here',
                type: 'entities',
                resource: 'priceList',
                nullable: true,
                description:
                  'Pick any specials this platform should honour. Leave empty and Happy Hour is skipped for them.',
              },
              {
                name: 'platformMerchantId',
                label: 'Your account number with the platform',
                type: 'text',
                nullable: true,
                description:
                  'The number the delivery platform gave you for this bar. It comes from them, not from here.',
              },
              {
                name: 'webhookEnabled',
                label: 'Let their orders come in automatically',
                type: 'boolean',
                // Says what turning it OFF does, because that is the use it gets: a platform
                // sending nonsense at two in the morning is stopped here without anybody having to
                // pull the bar off the platform altogether.
                description:
                  'Turn this off to stop accepting a platform’s orders while something is wrong at their end. Their sales can still be entered by hand.',
              },
            ]}
          />
        </Tabs.Panel>

        <Tabs.Panel value="arriving">
          <OrdersArriving />
        </Tabs.Panel>

        <Tabs.Panel value="receivables">
          <OwedByPlatform path={RECEIVABLES_PATH} />
          <CrudSection
            title="Owed to you"
            description="Sales a platform has already taken payment for but has not paid you yet. Each one drops off this list once it is covered by a payout."
            listPath={RECEIVABLES_PATH}
            emptyMessage="Nothing outstanding — every platform sale so far has been paid out."
            columns={[
              { accessor: 'at', title: 'When', unit: 'datetime' },
              { accessor: 'channelId', title: 'Platform', reference: 'channel' },
              { accessor: 'grossMinor', title: 'Amount', unit: 'money' },
              { accessor: 'kind', title: 'Type', enumGroup: 'receivableKind' },
            ]}
          />
        </Tabs.Panel>

        <Tabs.Panel value="settlements">
          <CrudSection
            title="Payouts"
            entityNoun="payout"
            description="When a platform sends a statement, record the dates it covers and everything they held back — the sales are matched up for you. Then record what actually landed in your account, and anything they kept quiet about shows up as a gap."
            listPath="/admin/v1/channels/settlements"
            createPath="/admin/v1/channels/settlements"
            rowLabel={(r) => `the payout covering ${formatDate(r.periodStart as string)}`}
            emptyMessage="No payouts recorded yet. Add one when a platform transfers money to your account."
            rowActions={(row, refresh) => <SettlementPaymentButton row={row} refresh={refresh} />}
            columns={[
              { accessor: 'channelId', title: 'Platform', reference: 'channel' },
              {
                // One period, not two columns: nobody reads a start and an end as separate facts.
                accessor: 'periodStart',
                title: 'Covers',
                noWrap: true,
                render: (r) =>
                  `${formatDate(r.periodStart as string)} – ${formatDate(r.periodEnd as string)}`,
              },
              { accessor: 'grossMatchedMinor', title: 'Sales covered', unit: 'money' },
              {
                // The charges were being recorded and then never shown, so the gap between what the
                // platform sold for you and what they sent had no explanation on screen.
                accessor: 'deductions',
                title: 'They held back',
                render: (r) => `−${formatMoney(heldBackMinor(r), (r.currency as string) ?? 'HNL')}`,
              },
              {
                accessor: 'expected',
                title: 'Should have arrived',
                render: (r) => formatMoney(expectedNetMinor(r), (r.currency as string) ?? 'HNL'),
              },
              { accessor: 'netReceivedMinor', title: 'Bank movement', unit: 'money' },
              { accessor: 'status', title: 'Result', render: (r) => <SettlementOutcome row={r} /> },
            ]}
            fields={[
              { name: 'channelId', label: 'Platform', type: 'entity', resource: 'channel', required: true },
              {
                name: 'periodStart',
                label: 'Covers from',
                type: 'date',
                required: true,
                description: 'The first day of sales this payment is for',
              },
              { name: 'periodEnd', label: 'Covers to', type: 'date', required: true },
              {
                name: 'statementRef',
                label: 'Their reference',
                type: 'text',
                nullable: true,
                description: 'The number on the platform’s statement, so you can find it again',
              },
              {
                name: 'deductions',
                label: 'What they held back',
                type: 'rows',
                rowNoun: 'charge',
                description:
                  'Every charge on their statement — commission, a delivery fee, a marketing contribution. These are what explain the gap between the sales covered and what actually arrived.',
                rowFields: [
                  {
                    name: 'label',
                    label: 'What it was',
                    type: 'text',
                    placeholder: 'Commission',
                  },
                  { name: 'amountMinor', label: 'Amount', type: 'number', unit: 'money' },
                ],
              },
            ]}
          />
        </Tabs.Panel>

        <Tabs.Panel value="mappings">
          <CrudSection
            title="Menu matching"
            entityNoun="match"
            description="Delivery platforms use their own names for your dishes. Match each one to your menu so their orders ring up correctly. Anything we could not match waits here for you."
            listPath="/admin/v1/channels/item-mappings"
            createPath="/admin/v1/channels/item-mappings"
            // Without this the queue was a dead end: an item nobody could match arrived here, the
            // tab told you it was waiting for you, and the only thing you could do to it was add a
            // second row saying the same thing.
            updatePathTemplate="/admin/v1/channels/item-mappings/{id}"
            defaults={{}}
            rowLabel={(r) => String(r.platformName ?? 'this item')}
            emptyMessage="Nothing to match yet."
            columns={[
              { accessor: 'channelId', title: 'Platform', reference: 'channel' },
              { accessor: 'platformName', title: 'Their name for it' },
              { accessor: 'platformSku', title: 'Their code' },
              // This is the whole point of the screen — what their name rings up as on your own
              // menu — and it could not be shown until sizes became listable. An unmatched row says
              // so here, which is why there is no separate status column: "Needs matching" and an
              // empty "rings up as" were the same fact printed twice.
              {
                accessor: 'variantId',
                title: 'Rings up as',
                render: (r) =>
                  r.variantId ? (
                    <EntityLabel entity="variant" id={r.variantId} />
                  ) : (
                    <Text size="sm" c="orange">
                      Needs matching
                    </Text>
                  ),
              },
            ]}
            fields={[
              {
                name: 'channelId',
                label: 'Platform',
                type: 'entity',
                resource: 'channel',
                required: true,
                // A match belongs to the platform that named the item, and the update refuses to
                // move it. Shown greyed rather than editable: a box that accepts a change the
                // server drops answers "Saved" over nothing happening.
                fixedAfterCreate: true,
              },
              {
                name: 'platformName',
                label: 'Their name for it',
                type: 'text',
                required: true,
                description: 'Exactly as it appears on the platform’s menu',
              },
              { name: 'platformSku', label: 'Their code', type: 'text', nullable: true },
              {
                name: 'variantId',
                label: 'Rings up as',
                type: 'entity',
                resource: 'variant',
                nullable: true,
                description: 'The size on your own menu this should sell as, so stock and takings are right',
              },
              // No status box: it is decided by whether a size was chosen above. Ticking "matched"
              // over an empty match is the one way to empty this queue without a single one of the
              // platform's orders ringing up correctly.
            ]}
          />
          {/* Under the matching table, because matching a name changes nothing on its own: the
              orders that already sold under it are still short until they are put back through. */}
          <LinesFromOrders />
        </Tabs.Panel>
      </Tabs>
    </>
  );
}
